import { useContext, useMemo } from 'react'

import { StatusCode } from 'shared/utils/route/route-utils'
import { SESSION_ROUTE, sessionRouteFor } from 'shared/utils/auth/embed'
import { Context as SessionContext } from './../client-session-context'
import { ClientSession } from './../utils'
import { retrySessionFetch } from 'shared/utils/retry/retry-session-fetch'
import { UserError, SystemError } from 'shared/utils/errors'

// Default cache duration of 5 minutes
const DEFAULT_CACHE_DURATION = 5 * 60 * 1000

/**
 * Helper utility for making standard requests to our session API.
 *
 * The route is `/main/app/(public)/auth/session`, or its embed counterpart
 * `/main/app/(public)/embed/session` when the embedded portal has called
 * {@link configureClientSessionRoute}.
 *
 * @throws If the response status is not 200
 */

interface SessionCache {
  promise: Promise<ClientSession | null>
  timestamp: number
  expiresAt: number
  data?: ClientSession | null
  isRefreshing: boolean
}

let sessionCache: SessionCache | null = null

/**
 * Module-level, like `sessionCache`, because the callers that matter are not
 * React: `makeWebTransport` and the gRPC retry interceptor both reach this
 * module from outside any component.
 */
let sessionRoute: string = SESSION_ROUTE

/**
 * Points the client session fetch at the cookie the current page actually has.
 * Called by `PortalEmbedProvider`; everything else keeps the strict route.
 *
 * The browser cannot read `EMBED_REQUEST_HEADER`, and `window.self !==
 * window.top` is not a substitute - a same-site iframe is framed too, and there
 * the strict cookie is the one that exists.
 *
 * Browser only: on the server this module is shared across concurrent requests,
 * so an embedded render would flip the route for unrelated users.
 */
export function configureClientSessionRoute(isEmbedded: boolean): void {
  if (typeof window === 'undefined') return

  const nextRoute = sessionRouteFor(isEmbedded)

  if (nextRoute === sessionRoute) return

  sessionRoute = nextRoute

  // anything cached from the other cookie describes a different login
  clearCache()
}

/** The route {@link makeSessionRequest} will hit. Exported for tests. */
export function getClientSessionRoute(): string {
  return sessionRoute
}

export function isCacheValid(cache: SessionCache): boolean {
  return Date.now() < cache.expiresAt
}

export function shouldRefreshCache(cache: SessionCache): boolean {
  return Date.now() > cache.expiresAt - 60000 // 1 minute before expiration
}

export function clearCache(): void {
  sessionCache = null
}

export async function makeSessionRequest(
  forceRefresh = false,
): Promise<ClientSession | null> {
  return retrySessionFetch(async () => {
    const url = forceRefresh ? `${sessionRoute}?refresh=true` : sessionRoute

    const headers = {
      'content-type': 'application/json',
      accept: 'application/json',
    }

    const response = await fetch(url, {
      headers,
      credentials: 'include',
    }).catch((error) => {
      // general network error
      return {
        status: StatusCode.InternalServerError,
        statusText: error.message,
        json: () => Promise.resolve({}),
      }
    })

    const { status, statusText } = response

    switch (status) {
      case StatusCode.Success: {
        const session = await response.json()

        // Defensive check: /auth/session should never return empty object with 200 status
        // If this happens, it indicates a bug in the route handler
        if (!session || !Object.keys(session).length) {
          throw new UserError('No session data found')
        }

        return session
      }
      case StatusCode.Forbidden:
        throw new UserError(statusText, { httpStatusCode: status })
      case StatusCode.Unauthorized:
        throw new UserError(statusText, { httpStatusCode: status })
      case StatusCode.InternalServerError:
        throw new SystemError('Internal error fetching client session', {
          httpStatusCode: 500,
        })
      default:
        throw new SystemError(
          `Unknown error fetching client session: ${statusText}`,
          { httpStatusCode: status },
        )
    }
  })
}

/**
 * This function is used to get the client session via a REST call to our Session Route Handler as
 * the browser environment does not have access to the encrypted session cookie. This has some basic
 * caching to prevent too many concurrent calls to that Session Route.
 *
 * WARNING: this will call our /auth/session API a lot. Don't use this as your default. We
 * have a SessionProvider for keeping a session in memory for client-side use. This method
 * should be used for keeping that context in sync.
 *
 * @returns The client session or null if the session is not found
 */
export function getClientSession(): Promise<ClientSession | null> {
  // Check if we have a valid cached promise
  if (sessionCache && isCacheValid(sessionCache)) {
    // If cache is getting stale, refresh in background
    if (shouldRefreshCache(sessionCache) && !sessionCache.isRefreshing) {
      refreshSessionInBackground()
    }
    return sessionCache.promise
  }

  // Clear invalid cache
  if (sessionCache && !isCacheValid(sessionCache)) {
    clearCache()
  }

  // Create new promise for concurrent requests
  const timestamp = Date.now()
  const expiresAt = timestamp + DEFAULT_CACHE_DURATION

  // Wrap makeSessionRequest to clear cache synchronously on error
  const sessionPromise = makeSessionRequest().catch((error) => {
    // Only clear cache on definitive auth failures (401/403, UserError)
    // These indicate the user's session is actually invalid and they need to re-authenticate.
    //
    // For transient errors (500, network errors), we preserve the cache so that:
    // 1. Concurrent calls to useSession() can still return the last valid session
    // 2. Navigation doesn't revert to degraded state during temporary outages
    // 3. SessionProvider can maintain user state while retries are exhausted
    //
    // Note: Retries are handled by retrySessionFetch wrapper, not by cache clearing.
    // This aligns with efforts to eliminate "logouts" from transient errors.
    if (error instanceof UserError) {
      clearCache()
    }
    throw error
  })

  // Cache the promise with metadata
  sessionCache = {
    promise: sessionPromise,
    timestamp,
    expiresAt,
    isRefreshing: false,
  }

  // Update cache metadata on successful resolution
  sessionPromise
    .then((data) => {
      if (sessionCache) {
        sessionCache.data = data
        sessionCache.expiresAt = data?.expiration ?? sessionCache.expiresAt
      }
    })
    .catch(() => {
      // Error already handled above, this just prevents unhandled rejection
    })

  return sessionPromise
}

let pendingForceRefresh: Promise<ClientSession | null> | null = null

export async function refreshClientSession(): Promise<ClientSession | null> {
  if (pendingForceRefresh) {
    return pendingForceRefresh
  }

  clearCache()
  const timestamp = Date.now()
  const defaultExpiresAt = timestamp + DEFAULT_CACHE_DURATION

  pendingForceRefresh = makeSessionRequest(true)
    .then((data) => {
      sessionCache = {
        promise: Promise.resolve(data),
        timestamp,
        expiresAt: data?.expiration ?? defaultExpiresAt,
        data,
        isRefreshing: false,
      }
      return data
    })
    .catch((error) => {
      if (error instanceof UserError) {
        clearCache()
      }
      throw error
    })
    .finally(() => {
      pendingForceRefresh = null
    })

  return pendingForceRefresh
}

async function refreshSessionInBackground(): Promise<void> {
  if (!sessionCache || sessionCache.isRefreshing) {
    return
  }

  sessionCache.isRefreshing = true

  try {
    const newData = await makeSessionRequest()

    // Update cache with new data
    if (sessionCache) {
      sessionCache.data = newData
      sessionCache.timestamp = Date.now()
      sessionCache.expiresAt =
        newData?.expiration ?? Date.now() + DEFAULT_CACHE_DURATION
      sessionCache.isRefreshing = false
    }
  } catch (error) {
    // On refresh failure, keep existing cache but mark as not refreshing
    // This preserves the last valid session data for concurrent callers,
    // preventing navigation from showing degraded state during transient errors.
    // Background refresh is best-effort; the main getClientSession() handles critical failures.
    if (sessionCache) {
      sessionCache.isRefreshing = false
    }
  }
}

/**
 * React hook for accessing the encrypted session cookie. This will
 * pull from context first, and then from the server if no session
 * or the session has expired
 * @deprecated use useSession() from shared/contexts/session-provider/session-provider instead
 * Note: this, makes a request to an API route as secure cookies are
 * not directly accessible to browser javascript
 */
export function useSession(): {
  session: ClientSession | null
  loading: boolean
  active: boolean
  error: boolean
} {
  if (!SessionContext) {
    throw new Error('React Context is unavailable in Server Components')
  }

  const context = useContext(SessionContext)

  if (!context) {
    throw new Error('useSession must be used within a SessionProvider')
  }

  return {
    session: context.session,
    loading: context.loading,
    active: context.active,
    error: context.error,
  }
}
/**
 * React hook for accessing the access token from the client session.
 * @deprecated use useSession() from shared/contexts/session-provider/session-provider instead
 * @returns The access token or undefined if the session is not found
 */
export function useAccessToken(): string | undefined {
  const context = useContext(SessionContext)

  if (!context) {
    throw new Error('useAccessToken must be used within a SessionProvider')
  }

  const accessToken = useMemo(
    () => context.session?.accessToken,
    [context.session?.accessToken],
  )

  return accessToken
}
